import React from "react";

export const FORFAIT_MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export const forfaitMonthsFromRow = (row) => row?.periodicita === "mensile"
  ? Array.from({ length: 12 }, (_, index) => index + 1)
  : String(row?.mesi_fatturazione || "").split(",").filter(Boolean).map(Number);

export const forfaitPeriodicityFromMonths = (months) => months.length === 12 ? "mensile" : "custom";

const euro = (value) => Number(value || 0).toLocaleString("it-IT", {
  style: "currency", currency: "EUR", minimumFractionDigits: 2,
});

const formatMonths = (value) => {
  if (!value) return "—";
  return String(value).split(",").map((month) => MONTH_NAMES[Number(month) - 1] || month).join(", ");
};

const formatCedoliniPeriod = (value) => {
  if (value && typeof value === "object") {
    const anno = Number(value.anno);
    const mese = Number(value.mese);
    if (anno && mese >= 1 && mese <= 12) {
      return new Date(anno, mese - 1, 1).toLocaleDateString("it-IT", {
        month: "long",
        year: "numeric",
      });
    }
  }
  const match = String(value || "").match(/^(\d{4})-(\d{2})/);
  if (!match) return "—";
  return new Date(Number(match[1]), Number(match[2]) - 1, 1).toLocaleDateString("it-IT", {
    month: "long",
    year: "numeric",
  });
};

export const ForfaitConfigurationForm = ({
  value,
  onChange,
  disabled = false,
  showHistory = false,
  onDeleteHistory,
  rateCard,
}) => {
  const months = value.mesi || [];
  const set = (field, fieldValue) => onChange({ ...value, [field]: fieldValue });

  return (
    <div className="space-y-3 text-sm">
      {rateCard && (
        <div className="grid grid-cols-2 gap-2 rounded border border-green-200 bg-green-50 p-2 text-xs md:grid-cols-4">
          <div><span className="block text-gray-500">Cedolini</span><strong>{rateCard.numeroCedolini ?? 0}</strong></div>
          <div><span className="block text-gray-500">Mese dati cedolini</span><strong className="capitalize">{formatCedoliniPeriod(rateCard.meseConsiderato)}</strong></div>
          <div><span className="block text-gray-500">Fascia</span><strong>{rateCard.fascia || "—"}</strong></div>
          <div><span className="block text-gray-500">Rate Card</span><strong>{rateCard.importo == null ? "—" : euro(rateCard.importo)}</strong></div>
        </div>
      )}

      <label className="block">Decorrenza (anno)
        <input type="number" step="1" min="2000" max="2100" value={value.decorrenza || ""} disabled={disabled}
          onChange={(event) => set("decorrenza", event.target.value ? Number(event.target.value) : "")}
          className="mt-1 w-full rounded border px-2 py-1 disabled:bg-gray-100" />
        {showHistory && <small className="text-xs text-gray-500">Anno da cui vale questa configurazione</small>}
      </label>

      <label className="block">Importo €
        <input type="number" step="0.01" min="0" value={value.importo ?? ""} disabled={disabled || value.importoBloccato}
          onChange={(event) => set("importo", event.target.value)}
          className="mt-1 w-full rounded border px-2 py-1 disabled:bg-gray-100" />
        {value.importoBloccato && <small className="text-xs text-gray-500">Fallback: normalmente calcolato dalla Rate Card</small>}
      </label>

      <div>
        <span className="mb-1 block">Mesi di fatturazione</span>
        <div className="flex flex-wrap gap-1">
          {FORFAIT_MONTHS.map((label, index) => {
            const month = index + 1;
            const active = months.includes(month);
            return (
              <button type="button" key={month} disabled={disabled}
                onClick={() => set("mesi", active ? months.filter((item) => item !== month) : [...months, month].sort((a, b) => a - b))}
                className={`rounded border px-1.5 py-0.5 text-xs disabled:cursor-not-allowed disabled:opacity-50 ${active ? "border-blue-500 bg-blue-100 text-blue-800" : "border-gray-300"}`}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <label className="block">Note
        <textarea value={value.note || ""} disabled={disabled} onChange={(event) => set("note", event.target.value)}
          className="mt-1 min-h-[50px] w-full rounded border px-2 py-1 disabled:bg-gray-100" />
      </label>

      {showHistory && (
        <div>
          <span className="mb-1 block font-semibold">Storico modifiche</span>
          {!value.storico ? <p className="text-gray-400">Caricamento...</p> : value.storico.length === 0 ? (
            <p className="text-gray-500">Nessuna modifica registrata.</p>
          ) : (
            <div className="max-h-48 space-y-1.5 overflow-auto">
              {value.storico.map((row, index) => (
                <div key={row.id} className="rounded border p-1.5 text-xs">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold">{new Date(row.created_at).toLocaleString("it-IT")} — {row.modificato_da || "—"}</div>
                    {index === 0 && !disabled && onDeleteHistory && (
                      <button type="button" onClick={() => onDeleteHistory(row)}
                        className="shrink-0 rounded border border-red-200 bg-red-50 px-1.5 py-0.5 font-semibold text-red-700 hover:bg-red-100">Elimina</button>
                    )}
                  </div>
                  <div>Importo: {euro(row.importo_precedente)} → {euro(row.importo_nuovo)}</div>
                  {(row.forzato_precedente || row.forzato_nuovo) ? <div>Importo forzato: {row.forzato_precedente ? "Sì" : "No"} → {row.forzato_nuovo ? "Sì" : "No"}</div> : null}
                  <div>Decorrenza: {row.decorrenza ? new Date(`${row.decorrenza}-01T12:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" }) : "—"}</div>
                  <div>Mesi: {formatMonths(row.mesi_precedenti)} → {formatMonths(row.mesi_nuovi)}</div>
                  <div>Note: {row.note_precedenti || "—"} → {row.note_nuove || "—"}</div>
                  {row.motivo && <div className="mt-1 text-gray-600">Motivo: {row.motivo}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ForfaitConfigurationForm;
